import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import WarningIcon from "@mui/icons-material/Warning"
import {
	Box,
	Collapse,
	Grid,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { useSelector } from "react-redux"
import { PlickState } from "../../../store/actions/file-detail"
import { PlickResponse } from "../../../store/actions/plick-detail"
import { RootState } from "../../../store/reducers"
import { isJsonValid } from "../../../utils/json-validator"
import Status from "../../status"
import PlickDrawer from "../drawer"
import PlickErrors from "../errors"

const PlickRow = ({ row }: { row: PlickState }) => {
	const { t } = useTranslation()

	const userPermissions = useSelector(
		(state: RootState) => state.auth?.user?.permissions
	)

	const hasCrudTransactionEnabled: boolean = userPermissions
		? userPermissions.some(
				(perm) =>
					perm.permissionName === "CRUD_TRANSACTION" && perm.enabled === true
			)
		: false

	const [openRow, setOpenRow] = useState<boolean>(false)
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
	const [currentPlick, setCurrentPlick] = useState<PlickResponse | undefined>(
		undefined
	)
	const isInError = row.status === "IN_ERROR" || !row.correctPlick

	const TableCellStyled = styled(TableCell)(() => ({
		paddingLeft: "8px",
	}))

	const TableRowStyled = styled(TableRow)(() => ({
		"&:hover": {
			backgroundColor: isInError
				? "rgba(211, 47, 47, 0.1) !important"
				: "inherit",
		},
	}))

	const handlePlickDetail = (plick: PlickResponse) => {
		setIsDrawerOpen(true)
		setCurrentPlick(plick)
	}

	return (
		<>
			<TableRowStyled
				hover
				tabIndex={-1}
				key={row.id}
				sx={{
					backgroundColor: isInError
						? "rgba(211, 47, 47, 0.1) !important"
						: openRow
							? "#F5F7FD"
							: "#FFF",
				}}
			>
				<TableCellStyled>
					<IconButton
						aria-label="expand row"
						size="small"
						onClick={() => setOpenRow(!openRow)}
					>
						{openRow ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</TableCellStyled>
				<TableCellStyled component="td" scope="row" padding="none">
					{row.transactionId}
				</TableCellStyled>
				<TableCellStyled align="left">{row.causal}</TableCellStyled>
				<TableCellStyled align="left">{row.notes}</TableCellStyled>
				<TableCellStyled align="left">{row.activationDate}</TableCellStyled>
				<TableCellStyled align="left">{row.receiverName}</TableCellStyled>
				<TableCellStyled align="right">{row.amount}€</TableCellStyled>
				<TableCellStyled align="left">
					<Status status={row.status} />
				</TableCellStyled>
				<TableCellStyled>
					{isInError && hasCrudTransactionEnabled && (
						<Tooltip title={t("buttons.fix", { ns: "plick" })}>
							<IconButton
								color="primary"
								aria-label="expand row"
								size="small"
								onClick={() => handlePlickDetail(row)}
							>
								<EditOutlinedIcon />
							</IconButton>
						</Tooltip>
					)}
					{isInError && (
						<Tooltip
							title={t("errors.tooltip.title", {
								ns: "plick",
								postProcess: "interval",
								count: row?.validationErrors?.split(" - ")?.length ?? 0,
								errorsNumber: row.validationErrors.split(" - ").length,
							})}
						>
							<IconButton
								color="primary"
								aria-label="expand row"
								size="small"
								sx={{ color: "#d32f2f" }}
								onClick={() => setOpenRow(!openRow)}
							>
								<WarningIcon />
							</IconButton>
						</Tooltip>
					)}
				</TableCellStyled>
			</TableRowStyled>
			<TableRow
				sx={{
					backgroundColor: isInError
						? "rgba(211, 47, 47, 0.1)"
						: "#F5F7FD",
				}}
			>
				<TableCell style={{ padding: "0" }} colSpan={9}>
					<Collapse in={openRow} timeout="auto" unmountOnExit>
						<Box>
							<PlickErrors errorListString={row.validationErrors} />
						</Box>
						<Grid
							container
							justifyContent={"space-between"}
							style={{ padding: "0" }}
						>
							<Grid
								item
								md={4}
								sx={{
									backgroundColor: isInError
										? "rgba(211, 47, 47, 0.1)"
										: "#F5F7FD",
									padding: "16px 8px",
								}}
							>
								<Typography variant="subtitle1" gutterBottom component="div">
									<b>Beneficiario</b>
								</Typography>
								<Grid container flexWrap={"wrap"} gap={"8px"}>
									<Grid item>
										<b>Canale: </b> {row.receiverDevice ?? "N/A"}
									</Grid>
									<Grid item>
										<b>Iban: </b> {row.receiverIban.trim() ?? "N/A"}
									</Grid>
									<Grid item>
										<b>Iban da banca: </b> {row.receiverIbanFromBank.toString()}
									</Grid>
									<Grid item>
										<b>Tipo: </b> {row.receiverType ?? "N/A"}
									</Grid>
								</Grid>
							</Grid>
							<Grid
								item
								md={4}
								sx={{
									backgroundColor: isInError
										? "rgba(211, 47, 47, 0.1)"
										: "#F5F7FD",
									padding: "16px 8px",
								}}
							>
								<Typography variant="subtitle1" gutterBottom component="div">
									<b>Mittente</b>
								</Typography>

								<Grid container flexWrap={"wrap"} gap={"8px"}>
									<Grid item>
										<b>id: </b> {row.senderId}
									</Grid>
									<Grid item>
										<b>Name: </b>{" "}
										{row.senderIsCompany
											? row.senderCompany
											: `${row.senderFirstname} ${row.senderLastname}`}
									</Grid>
									<Grid item>
										<b>Canale: </b> {row.senderDevice}
									</Grid>
									<Grid item>
										<b>Account: </b> {row.senderAccount}
									</Grid>
									<Grid item>
										<b>Lingua: </b> {row.senderLanguagePreferences ?? "N/A"}
									</Grid>
									<Grid item>
										<b>Extra info: </b>{" "}
										{isJsonValid(row.senderExtraInfo)
											? JSON.stringify(
													JSON.parse(row.senderExtraInfo),
													undefined,
													4
												)
											: "N/A"}
									</Grid>
								</Grid>
							</Grid>
							<Grid
								item
								md={4}
								sx={{
									backgroundColor: isInError
										? "rgba(211, 47, 47, 0.1)"
										: "#F5F7FD",
									padding: "16px 8px",
								}}
							>
								<Typography variant="subtitle1" gutterBottom component="div">
									<b>Configurazione</b>
								</Typography>
								<Grid container flexWrap={"wrap"} gap={"8px"}>
									<Grid item>
										<b>Pin: </b> {row.pin ?? "N/A"}
									</Grid>
									<Grid item>
										<b>Pin Type: </b> {row.pinType ?? "N/A"}
									</Grid>
									<Grid item>
										<b>CheckIban: </b> {row.checkIban.toString()}
									</Grid>
									<Grid item>
										<b>Codice checkiban: </b> {row.checkIbanCode ?? "N/A"}
									</Grid>
									<Grid item>
										<b>Stato checkiban: </b> {row.checkIbanLastStatus ?? "N/A"}
									</Grid>
									<Grid item>
										<b>Voucher: </b> {row.voucher.toString()}
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Collapse>
				</TableCell>
			</TableRow>
			<PlickDrawer
				currentPlick={currentPlick}
				isDrawerOpen={isDrawerOpen}
				setIsDrawerOpen={setIsDrawerOpen}
			/>
		</>
	)
}

export default PlickRow
